import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReportPreferences,
  upsertReportPreference,
  type ReportDisplayOptions,
} from '@/api/batchReportsApi';

const QUERY_KEY = ['reportPreferences'];
const STALE_TIME_MS = 5 * 60 * 1000;

export function useReportPreferences() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getReportPreferences,
    staleTime: STALE_TIME_MS,
  });

  const mutation = useMutation({
    mutationFn: ({
      templateId,
      displayOptions,
    }: {
      templateId: string;
      displayOptions: ReportDisplayOptions;
    }) => upsertReportPreference(templateId, displayOptions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const getPreference = (templateId: string): ReportDisplayOptions | null => {
    const prefs = query.data?.preferences ?? [];
    const found = prefs.find((p) => p.template_id === templateId);
    return found?.display_options ?? null;
  };

  const updatePreference = (
    templateId: string,
    displayOptions: ReportDisplayOptions
  ) => {
    return mutation.mutateAsync({ templateId, displayOptions });
  };

  return {
    preferences: query.data?.preferences ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    getPreference,
    updatePreference,
    isUpdating: mutation.isPending,
  };
}
